/**
 * Export actions. The Preview / Export / "Plot selection"
 * button row, plus the export job's progress bar and result readout.
 *
 * - Preview delegates to `runPreview()` (the `<data-preview>` card renders it).
 * - Export starts a `/export` job, polls `/jobs/{id}` every 500ms updating the
 *   progress bar, then fetches the job's `/download` URL with the current
 *   Microsoft bearer token and saves the returned S3 stream.
 * - "Plot selection" switches to the Plot tab with the numeric picks as Y series.
 *   The selection→y_left handoff is a seam until the plot area (§10.7) lands; for
 *   now it validates the picks and switches tabs.
 *
 * Buttons enable only once a ship's columns have loaded. Export reads EOS
 * only; another source hides the whole row via `.eos-only`.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  selectedShip,
  allColumns,
  selectedCols,
  activeTab,
} from '../../state/store.ts';
import { buildExportRequest } from '../../state/request.ts';
import { startExport, getJob } from '../../api/endpoints.ts';
import { apiResponse } from '../../api/client.ts';
import { isNumericType } from '../../utils/columns.ts';
import { formatBytes } from '../../utils/fmt.ts';
import { showToast } from '../../ui/toast.ts';
import { showError, hideError } from '../../ui/error-box.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import { setLineSelection } from '../../state/plot-state.ts';
import { doPlot } from '../plot/plot-actions.ts';
import { runPreview } from '../preview/preview-state.ts';
import { resumeSuspendedPeriodComparison } from '../../state/period-comparison.ts';
import { userMessage } from '../../api/errors.ts';

const POLL_MS = 500;
const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

@customElement('export-panel')
export class ExportPanel extends LightElement {
  @state() private previewing = false;
  @state() private downloading = false;
  @state() private progressActive = false;
  @state() private progressPct = 0;
  @state() private progressText = '';
  @state() private downloadInfo: TemplateResult | null = null;

  private store = new StoreController(this, [
    selectedShip,
    allColumns,
    selectedCols,
  ]);

  private get ready(): boolean {
    return !!selectedShip.value && allColumns.value.length > 0;
  }

  private get numericPicks(): string[] {
    return [...selectedCols.value].filter((name) => {
      const col = allColumns.value.find((c) => c.name === name);
      return col ? isNumericType(col.type) : false;
    });
  }

  /* ── Preview ──────────────────────────────────────────── */

  private async onPreview(): Promise<void> {
    this.previewing = true;
    try {
      await runPreview();
    } finally {
      this.previewing = false;
    }
  }

  /* ── Export job ───────────────────────────────────────── */

  private async onDownload(): Promise<void> {
    if (this.downloading) return;
    const req = buildExportRequest();
    if (!req) return;

    this.downloading = true;
    this.downloadInfo = null;
    this.progressActive = true;
    this.progressPct = 0;
    this.progressText = 'Preparing…';

    const start = performance.now();
    try {
      const { job_id } = await startExport(req);

      // Poll until the job finishes, mirroring the blob's 500ms loop.
      for (;;) {
        await sleep(POLL_MS);
        const job = await getJob(job_id);

        if (job.status === 'failed') {
          throw new Error(job.error || 'Export failed');
        }
        if (job.status === 'done') {
          this.progressPct = 100;
          this.progressText = 'Ready - starting download…';
          await this.triggerDownload(job_id, job.filename);
          const elapsed = ((performance.now() - start) / 1000).toFixed(1);
          this.progressActive = false;
          const sizeText = job.total_bytes ? formatBytes(job.total_bytes) : '';
          this.downloadInfo = html`Exported${sizeText
            ? html` <strong>${sizeText}</strong>`
            : nothing}
          in <span class="time">${elapsed}s</span>`;
          showToast('success', `Export complete${sizeText ? ' - ' + sizeText : ''} in ${elapsed}s`);
          hideError();
          break;
        }

        this.progressPct = job.progress_pct || 0;
        const rowsText = job.rows_processed?.toLocaleString() || '0';
        const estText = job.estimated_rows?.toLocaleString() || '?';
        this.progressText = `${rowsText} / ${estText} rows`;
      }
    } catch (e) {
      this.progressActive = false;
      this.downloadInfo = null;
      const msg = userMessage(e);
      showToast('error', 'Export failed: ' + msg);
      showError('Export failed: ' + msg);
    } finally {
      this.downloading = false;
    }
  }

  /** Fetch with bearer auth, then use an object URL for the browser save dialog. */
  private async triggerDownload(jobId: string, filename?: string): Promise<void> {
    const response = await apiResponse(`/jobs/${encodeURIComponent(jobId)}/download`, {
      timeoutMs: 120000,
    });
    const objectUrl = URL.createObjectURL(await response.blob());
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'export';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  /* ── Plot selection (Export → Plot hand-off, §10.6/§10.7) ── */

  private onPlotSelection(): void {
    if (this.numericPicks.length === 0) {
      showToast('error', 'Select at least one numeric column to plot');
      return;
    }
    setLineSelection(this.numericPicks); // plot_type=line, y_left=picks, y_right=[]
    activeTab.set('plot');
    document.body.dataset.activeTab = 'plot';
    resumeSuspendedPeriodComparison();
    syncToUrl();
    doPlot(); // §10.8 seam (no-op until the renderer lands)
  }

  override render(): TemplateResult {
    const busy = this.downloading;
    return html`
      <div class="download-row eos-only" data-tab="export">
        <button
          class="btn btn-secondary"
          ?disabled=${!this.ready || this.previewing || busy}
          @click=${this.onPreview}
        >
          ${this.previewing ? 'Loading…' : 'Preview'}
        </button>
        <button
          class="btn btn-primary"
          ?disabled=${!this.ready || busy}
          @click=${this.onDownload}
        >
          ${busy ? 'Exporting…' : 'Export'}
        </button>
        <button
          class="btn btn-secondary"
          title="Switch to Plot tab with these columns as Y-axis series"
          ?disabled=${this.numericPicks.length === 0}
          @click=${this.onPlotSelection}
        >
          Plot selection ›
        </button>
        ${this.downloadInfo
          ? html`<span class="download-info">${this.downloadInfo}</span>`
          : nothing}
      </div>
      <div class="progress-container ${this.progressActive ? 'active' : ''} eos-only" data-tab="export">
        <div class="progress-bar-track">
          <div class="progress-bar-fill" style="width:${this.progressPct}%"></div>
        </div>
        <span class="progress-text">${this.progressText}</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'export-panel': ExportPanel;
  }
}
